import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { interviewsApi, usersApi } from '../../services/api';
import Modal from '../shared/Modal';
import toast from 'react-hot-toast';
import { AlertCircle, RotateCcw, Trash2 } from 'lucide-react';

interface Props {
  interview: any;
  onClose: () => void;
}

export default function ScheduleInterviewModal({ interview, onClose }: Props) {
  const isEdit = interview.status === 'scheduled';

  const [assignedTo, setAssignedTo] = useState(interview.assigned_to || '');
  const [date, setDate] = useState(interview.scheduled_date || '');
  const [startTime, setStartTime] = useState(interview.scheduled_start_time || '');
  const [endTime, setEndTime] = useState(interview.scheduled_end_time || '');
  const [hasConflict, setHasConflict] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const queryClient = useQueryClient();
  const { data: usersData } = useQuery({ queryKey: ['users'], queryFn: () => usersApi.list() });
  const users = usersData?.data || [];

  // Exclude Secretários — only Presidente and Conselheiros conduct interviews
  const interviewers = users.filter((u: any) => !u.role_label?.includes('Secretário'));

  const checkConflicts = async () => {
    if (!assignedTo || !date || !startTime || !endTime) return;
    try {
      const result = await interviewsApi.checkConflicts({
        assigned_to: assignedTo, date, start: startTime, end: endTime,
        exclude_id: interview.id,
      });
      setHasConflict(result.has_conflicts);
    } catch { setHasConflict(false); }
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setStartTime(value);
    if (value) {
      const [h, m] = value.split(':').map(Number);
      const total = h * 60 + m + 30;
      const eh = Math.floor(total / 60) % 24;
      const em = total % 60;
      setEndTime(`${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`);
    }
  };

  const mutation = useMutation({
    mutationFn: () => interviewsApi.schedule(interview.id, {
      assigned_to: assignedTo,
      scheduled_date: date,
      scheduled_start_time: startTime,
      scheduled_end_time: endTime,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success(isEdit ? 'Entrevista atualizada!' : 'Entrevista agendada!');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const revertMutation = useMutation({
    mutationFn: () => interviewsApi.revert(interview.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Entrevista voltou para pendentes');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => interviewsApi.delete(interview.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interviews'] });
      toast.success('Entrevista excluída');
      onClose();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); mutation.mutate(); };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? `Editar entrevista — ${interview.person_name}` : `Agendar entrevista — ${interview.person_name}`}
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancelar</button>
          <button type="submit" form="schedule-form" disabled={!assignedTo || !date || !startTime || !endTime || mutation.isPending} className="btn-primary flex-1">
            {mutation.isPending ? (isEdit ? 'Salvando...' : 'Agendando...') : (isEdit ? 'Salvar' : 'Agendar')}
          </button>
        </div>
      }
    >
      <form id="schedule-form" onSubmit={handleSubmit} className="space-y-4">
        {hasConflict && (
          <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0 text-yellow-600" />
            <p>Atenção: existe conflito de horário com outra entrevista desta pessoa.</p>
          </div>
        )}
        <div>
          <label className="label">Quem vai entrevistar <span className="text-red-500">*</span></label>
          <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className="input" required>
            <option value="">Selecione...</option>
            {interviewers.map((u: any) => <option key={u.id} value={u.id}>{u.name} — {u.role_label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Data <span className="text-red-500">*</span></label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} onBlur={checkConflicts} className="input" required />
        </div>
        <div>
          <label className="label">Início <span className="text-red-500">*</span></label>
          <input type="time" value={startTime} onChange={handleStartTimeChange} onBlur={checkConflicts} className="input" required />
        </div>
        <div>
          <label className="label">Término <span className="text-red-500">*</span></label>
          <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} onBlur={checkConflicts} className="input" required />
        </div>
      </form>

      {isEdit && (
        <div className="mt-6 pt-4 border-t border-gray-100 space-y-2">
          <button
            onClick={() => revertMutation.mutate()}
            disabled={revertMutation.isPending}
            className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg text-gray-600 bg-gray-50 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RotateCcw size={14} />
            {revertMutation.isPending ? 'Aguarde...' : 'Marcar como pendente'}
          </button>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <Trash2 size={14} />
              Excluir
            </button>
          ) : (
            <div className="p-3 bg-red-50 rounded-lg space-y-2">
              <p className="text-sm text-red-700 text-center">Confirmar exclusão desta entrevista?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 text-sm py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  className="flex-1 text-sm py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {deleteMutation.isPending ? 'Excluindo...' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
